<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { NModal, NSpin, NEmpty } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useAgentSessionDevcontainerInfo } from '../composables/useAgentSessionDevcontainerInfo'
import { shortThread } from '../composables/shortThread'
import type { AgentSessionSummary } from '../stores/agent/types'

// Read-only session/lifecycle detail behind a row's info button (#1501).
// Deliberately does NOT show CPU/mem/disk/CLI-version the way
// AgentRepoDevcontainerStatus.vue does for a repo card — those are
// per-REPO metrics from the box's periodic sweep, and a repo's devcontainer
// can be shared or re-associated across sessions/slots (#1352), so
// attributing them to one session here would misrepresent whose container
// they describe. The repo link below sends an admin to that real picture
// instead of faking a per-session version of it.
const props = defineProps<{ session: AgentSessionSummary | null }>()
const emit = defineEmits<{ close: [] }>()

const threadId = computed(() => props.session?.thread_id ?? null)
const { info, loading } = useAgentSessionDevcontainerInfo(threadId)
const { formatAbsoluteDate } = useFormattedDate()

function onUpdateShow(show: boolean) {
  if (!show) emit('close')
}
</script>

<template>
  <NModal
    :show="session !== null"
    preset="card"
    :title="`Devcontainer info — ${session ? session.name || shortThread(session.thread_id) : ''}`"
    style="width: 480px"
    data-testid="agent-session-devcontainer-modal"
    @update:show="onUpdateShow"
  >
    <div v-if="loading" class="flex justify-center py-6" data-testid="agent-session-devcontainer-loading">
      <NSpin size="small" />
    </div>
    <NEmpty
      v-else-if="!info"
      size="small"
      data-testid="agent-session-devcontainer-empty"
      description="No devcontainer info available — the session never started on the agent box, its container was already stopped and cleaned up, or the box is unreachable right now."
    />
    <dl
      v-else
      class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm"
      data-testid="agent-session-devcontainer-fields"
    >
      <dt class="text-ink-faint">Repo</dt>
      <dd class="m-0 font-mono">{{ info.repo }}</dd>
      <dt class="text-ink-faint">Workspace</dt>
      <dd class="m-0 font-mono break-all">{{ info.workspace }}</dd>
      <dt class="text-ink-faint">Slot</dt>
      <dd class="m-0">{{ info.slot ?? '—' }}</dd>
      <dt class="text-ink-faint">Process PID</dt>
      <dd class="m-0">{{ info.process_pid ?? '—' }}</dd>
      <dt class="text-ink-faint">Cancel requested</dt>
      <dd class="m-0">{{ info.cancel_requested ? 'Yes' : 'No' }}</dd>
      <dt class="text-ink-faint">Last activity</dt>
      <dd class="m-0">{{ formatAbsoluteDate(info.last_activity_at) }}</dd>
      <dt class="text-ink-faint">Container stopped</dt>
      <dd class="m-0">{{ info.container_stopped_at ? formatAbsoluteDate(info.container_stopped_at) : '—' }}</dd>
      <dt class="text-ink-faint">Run completed</dt>
      <dd class="m-0">{{ info.run_completed_at ? formatAbsoluteDate(info.run_completed_at) : '—' }}</dd>
    </dl>

    <template v-if="info" #footer>
      <RouterLink
        :to="{ name: 'adminAgentRepos', query: { repo: info.repo } }"
        class="text-xs text-ink-muted no-underline hover:underline"
        data-testid="agent-session-devcontainer-repo-link"
        @click="emit('close')"
        >View {{ info.repo }}'s devcontainer status →</RouterLink
      >
    </template>
  </NModal>
</template>
